import React, { useState, useEffect, useMemo, useRef } from "react";
import { Box, Typography, GlobalStyles, Tooltip } from "@mui/material";
import { format, isValid } from "date-fns";
import {
  Message,
  MessageTypeColor,
  QuizTabStatus,
  TabbedMessageContent,
  TabStatus,
} from "../../../../types";
import { ReactionAndFavorite } from "../reaction-and-favorite/reaction-and-favorite";
import {
  ContentBlockRenderer,
  getTabPreviewText,
} from "./content-block-renderer";
import { normalizeTabbedContent, tabHasContent } from "./normalize";
import { useTabbedMessageStyles, pulseKeyframes } from "./styles";
import { useChatStore } from "../../../../stores/chatStore";
import { CustomMarkdown } from "../../../custom-markdown/custom-markdown";
import { isSuggestionTabLabel } from "../../../../helpers/messageHelpers";
import { openCsuSegmentation } from "../../../../axios/integration";
import { toast } from "react-toastify";
import { ExternalLink } from "lucide-react";

const CSU_APP_FORWARD_HINT = "Click the content to open CSU Segmentation & Adoption Ladder";

export function getNormalizedTabbedTabs(message: Message): TabbedMessageContent[] {
  const msg = message as unknown as Record<string, unknown>;
  const raw =
    message.tabbed_content ??
    msg.tabs ??
    (Array.isArray(msg.content) ? msg.content : null) ??
    [];
  return normalizeTabbedContent(raw);
}

export type ActiveTabInfo = {
  tabId: string;
  tabLabel: string;
  tabType: string;
};

export function getTabType(tab: TabbedMessageContent): string {
  const blocks = tab.content_blocks ?? [];
  const first = blocks[0] as { type?: string } | undefined;
  const raw = first?.type;
  if (typeof raw === "string" && raw.length > 0) {
    return raw.trim().toLowerCase();
  }
  return "text";
}

type QuizTabStatusBadgeConfig = {
  title: string;
  symbol: string;
  color: string;
  borderColor: string;
  backgroundColor: string;
  shadowColor: string;
};

const QUIZ_BADGE_CONFIG: Record<QuizTabStatus, QuizTabStatusBadgeConfig> = {
  new: {
    title: "New",
    symbol: "🕒",
    color: "#1D4ED8",
    borderColor: "#1D4ED8",
    backgroundColor: "#DBEAFE",
    shadowColor: "rgba(29, 78, 216, 0.45)",
  },
  correct: {
    title: "Correct answer",
    symbol: "✓",
    color: "#15803D",
    borderColor: "#16A34A",
    backgroundColor: "#DCFCE7",
    shadowColor: "rgba(22, 163, 74, 0.45)",
  },
  incorrect: {
    title: "Not correct answer",
    symbol: "!",
    color: "#B91C1C",
    borderColor: "#DC2626",
    backgroundColor: "#FEE2E2",
    shadowColor: "rgba(220, 38, 38, 0.45)",
  },
};

const INTERACTIVE_BLOCK_TYPES = new Set([
  "fill_in_blank",
  "ordering",
  "matching",
  "ordered_response",
  "quiz",
  "buttons",
]);

function isQuizTab(tab: TabbedMessageContent): boolean {
  const label = tab.tab_label?.toLowerCase().trim() ?? "";
  if (label.includes("quiz")) return true;
  return (tab.content_blocks ?? []).some((block) =>
    INTERACTIVE_BLOCK_TYPES.has(block.type),
  );
}

function getQuizStatusFromInteractive(
  tab: TabbedMessageContent,
): QuizTabStatus | null {
  const block = (tab.content_blocks ?? []).find(
    (b) =>
      b.type === "fill_in_blank" ||
      b.type === "ordering" ||
      b.type === "matching" ||
      b.type === "ordered_response",
  );
  if (!block) return null;
  if (block.state !== "disable") return "new";
  if (block.is_correct === true) return "correct";
  if (block.is_correct === false) return "incorrect";
  if (tab.quiz_status && tab.quiz_status !== "new") return tab.quiz_status;
  return "incorrect";
}

function getQuizStatusFromButtons(tab: TabbedMessageContent): QuizTabStatus | null {
  const quizBlock = (tab.content_blocks ?? []).find(
    (block) => block.type === "quiz" || block.type === "buttons",
  );
  if (!quizBlock) return null;

  const rows = quizBlock.buttons ?? [];
  if (!rows.length) return null;

  const hasChosenCorrect = rows.some((row) =>
    Array.isArray(row)
      ? row.some(
          (button: { isChosen?: boolean; isCorrect?: boolean } | undefined) =>
            !!button?.isChosen && !!button?.isCorrect,
        )
      : false,
  );
  if (hasChosenCorrect) return "correct";

  const hasChosenWrong = rows.some((row) =>
    Array.isArray(row)
      ? row.some(
          (button: { isChosen?: boolean; isCorrect?: boolean } | undefined) =>
            !!button?.isChosen && !button?.isCorrect,
        )
      : false,
  );
  if (hasChosenWrong) return "incorrect";

  const hasChosenAnswer = rows.some((row) =>
    Array.isArray(row)
      ? row.some(
          (button: { isChosen?: boolean; isCorrect?: boolean } | undefined) =>
            !!button?.isChosen,
        )
      : false,
  );
  if (hasChosenAnswer) return "incorrect";

  return "new";
}

function getQuizTabStatus(tab: TabbedMessageContent): QuizTabStatus | null {
  if (!isQuizTab(tab)) return null;
  if (tab.quiz_status) return tab.quiz_status;

  const statusFromInteractive = getQuizStatusFromInteractive(tab);
  if (statusFromInteractive) return statusFromInteractive;

  const statusFromButtons = getQuizStatusFromButtons(tab);
  if (statusFromButtons) return statusFromButtons;

  return "new";
}

type ActiveTabPanelProps = {
  tab: TabbedMessageContent;
  status: TabStatus;
  message: Message;
  showGlobalTitleChip: boolean;
  isBot: boolean;
  variant: MessageTypeColor;
  currentReaction?: string;
  isSaved?: boolean;
  markedIds: Record<string, string>;
  isTags?: string[];
  handleReactionClick: (reactionKey: string) => void;
  handleAddToFavorite: () => void;
  handleRemoveTag: (tag: string) => Promise<void>;
  setLargeHigth?: React.Dispatch<React.SetStateAction<boolean>>;
  onActiveFlashCardTextChange?: React.Dispatch<React.SetStateAction<string>>;
  onActiveFlippingCardTextChange?: React.Dispatch<React.SetStateAction<string>>;
  nextMessage?: Message;
  onQuizAnswered: (tabId: string) => void;
  pendingPlaceholderSx: object;
  loadingIndicatorSx: object;
  loadingDotsSx: object;
};

//Renders a single tab body. Mounted only while that tab is active.
const ActiveTabPanel = React.memo(function ActiveTabPanel({
  tab,
  status,
  message,
  showGlobalTitleChip,
  isBot,
  variant,
  currentReaction,
  isSaved,
  markedIds,
  isTags,
  handleReactionClick,
  handleAddToFavorite,
  handleRemoveTag,
  setLargeHigth,
  onActiveFlashCardTextChange,
  onActiveFlippingCardTextChange,
  nextMessage,
  onQuizAnswered,
  pendingPlaceholderSx,
  loadingIndicatorSx,
  loadingDotsSx,
}: ActiveTabPanelProps) {
  if (status === "pending") {
    return (
      <Box sx={pendingPlaceholderSx}>
        <Typography variant="body2">Waiting for content...</Typography>
      </Box>
    );
  }

  return (
    <>
      <ContentBlockRenderer
        blocks={tab.content_blocks ?? []}
        message={message}
        showGlobalTitleChip={showGlobalTitleChip}
        activeTabId={tab.tab_id}
        activeTabLabel={tab.tab_label}
        isBot={isBot}
        variant={variant}
        currentReaction={currentReaction}
        isSaved={isSaved}
        markedIds={markedIds}
        isTags={isTags}
        handleReactionClick={handleReactionClick}
        handleAddToFavorite={handleAddToFavorite}
        handleRemoveTag={handleRemoveTag}
        setLargeHigth={setLargeHigth}
        setActiveFlashCardText={onActiveFlashCardTextChange}
        setActiveFlippingCardText={onActiveFlippingCardTextChange}
        nextMessage={nextMessage}
        onQuizAnswered={onQuizAnswered}
        tabLevelTranscription={tab.transcription}
        tabLevelTranscriptionTimed={tab.transcription_timed}
      />
      {status === "streaming" && (
        <Box sx={loadingIndicatorSx}>
          <Box sx={loadingDotsSx}>
            <Box component="span" />
            <Box component="span" />
            <Box component="span" />
          </Box>
        </Box>
      )}
    </>
  );
});

export interface TabbedMessageProps {
  message: Message;
  isBot: boolean;
  variant: MessageTypeColor;
  currentReaction?: string;
  isSaved?: boolean;
  markedIds: Record<string, string>;
  isTags?: string[];
  handleReactionClick: (reactionKey: string) => void;
  handleAddToFavorite: () => void;
  handleRemoveTag: (tag: string) => Promise<void>;
  onActiveTabContentChange?: (content: string) => void;
  onActiveTabChange?: (info: ActiveTabInfo) => void;
  onActiveFlashCardTextChange?: React.Dispatch<React.SetStateAction<string>>;
  onActiveFlippingCardTextChange?: React.Dispatch<React.SetStateAction<string>>;
  nextMessage?: Message;
  setLargeHigth?: React.Dispatch<React.SetStateAction<boolean>>;
}

export const TabbedMessage: React.FC<TabbedMessageProps> = ({
  message,
  isBot,
  variant,
  currentReaction,
  isSaved,
  markedIds,
  isTags,
  handleReactionClick,
  handleAddToFavorite,
  handleRemoveTag,
  onActiveTabContentChange,
  onActiveTabChange,
  onActiveFlashCardTextChange,
  onActiveFlippingCardTextChange,
  nextMessage,
  setLargeHigth = () => {},
}) => {
  const styles = useTabbedMessageStyles(isBot, variant);
  const { targetMessageId, targetMessageTabId, targetMessageRequestSeq } =
    useChatStore();
  const [activeTabId, setActiveTabId] = useState<string | null>(null);
  const [pendingFeedbackFromTabId, setPendingFeedbackFromTabId] = useState<
    string | null
  >(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const tabsContainerRef = useRef<HTMLDivElement | null>(null);
  const tabRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const shouldScrollToAutoFeedbackRef = useRef(false);
  const lastAppliedReplyJumpRef = useRef<string | null>(null);
  const hasUserManuallySelectedTabRef = useRef(false);
  const autoFollowedFirstTabIdRef = useRef<string | null>(null);

  const tabbedContent = useMemo(
    () => getNormalizedTabbedTabs(message),
    [message],
  );

  const isStreaming = !!message.isStreaming;
  const forcedReplyTabId =
    targetMessageId === message.message_id ? targetMessageTabId : null;
  const forcedReplyJumpKey = forcedReplyTabId
    ? `${message.message_id}:${forcedReplyTabId}:${targetMessageRequestSeq}`
    : null;

  const getTabStatus = (tab: TabbedMessageContent): TabStatus => {
    if (!isStreaming) return "completed";
    if (!tabHasContent(tab)) return "pending";
    return tabbedContent.every(tabHasContent) ? "completed" : "streaming";
  };

  useEffect(() => {
    if (activeTabId || tabbedContent.length === 0) return;
    const id = tabbedContent[0]?.tab_id ?? null;
    autoFollowedFirstTabIdRef.current = id;
    setActiveTabId(id);
  }, [tabbedContent, activeTabId]);

  useEffect(() => {
    if (tabbedContent.length === 0) return;
    const firstTabId = tabbedContent[0]?.tab_id ?? null;
    if (!firstTabId) return;

    if (!activeTabId) {
      autoFollowedFirstTabIdRef.current = firstTabId;
      setActiveTabId(firstTabId);
      return;
    }

    const hasActiveTab = tabbedContent.some((tab) => tab.tab_id === activeTabId);
    if (!hasActiveTab) {
      autoFollowedFirstTabIdRef.current = firstTabId;
      setActiveTabId(firstTabId);
      return;
    }

    if (activeTabId === firstTabId) {
      autoFollowedFirstTabIdRef.current = firstTabId;
      return;
    }

    if (hasUserManuallySelectedTabRef.current) return;
    if (autoFollowedFirstTabIdRef.current !== activeTabId) return;

    autoFollowedFirstTabIdRef.current = firstTabId;
    setActiveTabId(firstTabId);
  }, [tabbedContent, activeTabId]);

  useEffect(() => {
    if (!forcedReplyTabId || !forcedReplyJumpKey || tabbedContent.length === 0) return;
    if (lastAppliedReplyJumpRef.current === forcedReplyJumpKey) return;
    const hasForcedTab = tabbedContent.some((tab) => tab.tab_id === forcedReplyTabId);
    if (!hasForcedTab) return;
    lastAppliedReplyJumpRef.current = forcedReplyJumpKey;
    setActiveTabId(forcedReplyTabId);
  }, [forcedReplyTabId, forcedReplyJumpKey, tabbedContent]);

  useEffect(() => {
    if (!pendingFeedbackFromTabId) return;

    const sourceTabIndex = tabbedContent.findIndex(
      (tab) => tab.tab_id === pendingFeedbackFromTabId,
    );
    if (sourceTabIndex < 0) {
      setPendingFeedbackFromTabId(null);
      return;
    }

    const feedbackTab = tabbedContent
      .slice(sourceTabIndex + 1)
      .find((tab) => tab.tab_label?.toLowerCase().includes("feedback"));

    if (!feedbackTab) return;
    if (!tabHasContent(feedbackTab)) return;

    if (feedbackTab.tab_id !== activeTabId) {
      shouldScrollToAutoFeedbackRef.current = true;
      setActiveTabId(feedbackTab.tab_id);
    }
    setPendingFeedbackFromTabId(null);
  }, [pendingFeedbackFromTabId, tabbedContent, activeTabId]);

  useEffect(() => {
    if (!activeTabId || !shouldScrollToAutoFeedbackRef.current) return;

    requestAnimationFrame(() => {
      const activeTabElement = tabRefs.current[activeTabId];
      activeTabElement?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "start",
      });
      wrapperRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest",
      });
      shouldScrollToAutoFeedbackRef.current = false;
    });
  }, [activeTabId]);

  useEffect(() => {
    if (!activeTabId || tabbedContent.length === 0) return;

    const activeIndex = tabbedContent.findIndex(
      (tab) => tab.tab_id === activeTabId,
    );
    if (activeIndex < 0) return;

    const lastFeedbackIndex = (() => {
      for (let i = tabbedContent.length - 1; i >= 0; i--) {
        const label = tabbedContent[i]?.tab_label?.toLowerCase().trim() ?? "";
        if (label.includes("feedback")) return i;
      }
      return -1;
    })();

    if (activeIndex !== lastFeedbackIndex) return;
    if (lastFeedbackIndex >= tabbedContent.length - 1) return;

    requestAnimationFrame(() => {
      const tabsContainer = tabsContainerRef.current;
      if (!tabsContainer) return;
      tabsContainer.scrollTo({
        left: tabsContainer.scrollWidth,
        behavior: "smooth",
      });
    });
  }, [activeTabId, tabbedContent]);

  const activeTab = useMemo(
    () => tabbedContent.find((t) => t.tab_id === activeTabId),
    [tabbedContent, activeTabId],
  );
  const activeTabStatus = activeTab ? getTabStatus(activeTab) : "pending";
  const isOpeningCsuHeatMapRef = useRef(false);

  useEffect(() => {
    const onPageShow = () => {
      isOpeningCsuHeatMapRef.current = false;
    };

    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  const handleCsuAppForwardClick = async () => {
    if (!activeTab?.csu_app_forward || isOpeningCsuHeatMapRef.current) return;

    isOpeningCsuHeatMapRef.current = true;
    try {
      await openCsuSegmentation();
    } catch (error) {
      console.error("Failed to open CSU Segmentation:", error);
      toast.error("Could not open CSU Segmentation. Please try again.");
    } finally {
      isOpeningCsuHeatMapRef.current = false;
    }
  };
  const messageTitle = (message.title || "").trim();
  const showTitleNearActiveTab =
    !!messageTitle && !!activeTab && tabHasContent(activeTab);

  useEffect(() => {
    const tab =
      activeTabId != null
        ? tabbedContent.find((t) => t.tab_id === activeTabId)
        : null;
    if (tab) {
      const hasFlashCard = tab.content_blocks?.some(
        (b) => b.type === "flash_card",
      );
      const hasFlippingCard = tab.content_blocks?.some(
        (b) => b.type === "flipping_card",
      );
      if (!hasFlashCard) onActiveFlashCardTextChange?.("");
      if (!hasFlippingCard) onActiveFlippingCardTextChange?.("");
      onActiveTabContentChange?.(getTabPreviewText(tab));
      onActiveTabChange?.({
        tabId: tab.tab_id,
        tabLabel: tab.tab_label,
        tabType: getTabType(tab),
      });
    } else {
      onActiveFlashCardTextChange?.("");
      onActiveFlippingCardTextChange?.("");
      onActiveTabContentChange?.("");
    }
  }, [
    activeTabId,
    tabbedContent,
    onActiveTabContentChange,
    onActiveTabChange,
    onActiveFlashCardTextChange,
    onActiveFlippingCardTextChange,
  ]);

  if (!tabbedContent.length) return null;

  const messageDate = message.date ? new Date(message.date) : null;
  const formattedDateParts =
    messageDate && isValid(messageDate)
      ? {
          dayMonth: format(messageDate, "dd/MM/yy"),
          time: format(messageDate, "HH:mm"),
        }
      : null;

  return (
    <>
      <GlobalStyles styles={pulseKeyframes} />
      <Box sx={styles.wrapper} ref={wrapperRef} data-tabbed-message>
        <Box sx={styles.tabsContainer} ref={tabsContainerRef}>
          {tabbedContent.map((tab) => {
            const isActive = tab.tab_id === activeTabId;
            const isSuggestionTab = isSuggestionTabLabel(tab.tab_label);
            // Headers only — never mount inactive tab bodies. Badge may read
            // quiz_status / light block metadata, but no ContentBlockRenderer.
            const quizStatus = getQuizTabStatus(tab);
            const quizBadgeConfig =
              quizStatus !== null ? QUIZ_BADGE_CONFIG[quizStatus] : null;
            const showQuizStatusBadge =
              Boolean(quizBadgeConfig) && !isSuggestionTab;
            const tabNode = (
              <Box
                key={tab.tab_id}
                ref={(element) => {
                  tabRefs.current[tab.tab_id] =
                    element as HTMLDivElement | null;
                }}
                sx={[
                  styles.tab,
                  ...(isActive ? [styles.tabActive] : []),
                  ...(tab.csu_app_forward
                    ? [
                        {
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                        },
                      ]
                    : []),
                ]}
                onClick={() => {
                  hasUserManuallySelectedTabRef.current = true;
                  setActiveTabId(tab.tab_id);
                  onActiveTabContentChange?.(getTabPreviewText(tab));
                  onActiveTabChange?.({
                    tabId: tab.tab_id,
                    tabLabel: tab.tab_label,
                    tabType: getTabType(tab),
                  });
                }}
              >
                {tab.tab_label}
                {tab.csu_app_forward && (
                  <ExternalLink size={14} strokeWidth={2} aria-hidden />
                )}
                {showQuizStatusBadge && quizBadgeConfig && (
                  <Box
                    component="span"
                    sx={{
                      position: "absolute",
                      top: "2px",
                      right: "2px",
                      width: { xs: "13px", sm: "16px" },
                      height: { xs: "13px", sm: "16px" },
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "50%",
                      border: `2px solid ${quizBadgeConfig.borderColor}`,
                      color: quizBadgeConfig.color,
                      backgroundColor: quizBadgeConfig.backgroundColor,
                      fontSize: { xs: "10px", sm: "12px" },
                      fontWeight: 700,
                      lineHeight: 1,
                      boxSizing: "border-box",
                      boxShadow: `0 0 0 2px ${quizBadgeConfig.shadowColor}`,
                    }}
                  >
                    {quizBadgeConfig.symbol}
                  </Box>
                )}
              </Box>
            );

            const tabWithHint =
              showQuizStatusBadge && quizBadgeConfig ? (
                <Tooltip
                  key={`tooltip-${tab.tab_id}`}
                  title={quizBadgeConfig.title}
                  arrow
                >
                  {tabNode}
                </Tooltip>
              ) : tab.csu_app_forward ? (
                <Tooltip
                  key={`tooltip-csu-${tab.tab_id}`}
                  title={CSU_APP_FORWARD_HINT}
                  arrow
                  slotProps={{
                    tooltip: {
                      sx: {
                        maxWidth: "none",
                        whiteSpace: "nowrap",
                      },
                    },
                  }}
                >
                  {tabNode}
                </Tooltip>
              ) : (
                tabNode
              );

            return (
              <React.Fragment key={`tab-with-title-${tab.tab_id}`}>
                {tabWithHint}
                {isActive && showTitleNearActiveTab && (
                  <Box sx={styles.messageTitleChip}>
                    <CustomMarkdown skipHtml>{messageTitle}</CustomMarkdown>
                  </Box>
                )}
              </React.Fragment>
            );
          })}
        </Box>

        <Box sx={styles.container}>
          {/*
            Mount only the active tab body. key=tab_id unmounts the previous
            panel on switch so images/markdown/diagrams are released.
          */}
          <Box
            sx={{
              ...styles.tabContent,
              ...(activeTab?.csu_app_forward ? { cursor: "pointer" } : null),
            }}
            data-tabbed-scroll
            onClick={
              activeTab?.csu_app_forward ? handleCsuAppForwardClick : undefined
            }
          >
            {activeTab?.csu_app_forward && (
              <Typography
                sx={{
                  mb: 1,
                  fontSize: { xs: "11px", sm: "12px" },
                  opacity: 0.7,
                  fontFamily: "Red Hat Display",
                }}
              >
                {CSU_APP_FORWARD_HINT}
              </Typography>
            )}
            {activeTab ? (
              <ActiveTabPanel
                key={activeTab.tab_id}
                tab={activeTab}
                status={activeTabStatus}
                message={message}
                showGlobalTitleChip={showTitleNearActiveTab}
                isBot={isBot}
                variant={variant}
                currentReaction={currentReaction}
                isSaved={isSaved}
                markedIds={markedIds}
                isTags={isTags}
                handleReactionClick={handleReactionClick}
                handleAddToFavorite={handleAddToFavorite}
                handleRemoveTag={handleRemoveTag}
                setLargeHigth={setLargeHigth}
                onActiveFlashCardTextChange={onActiveFlashCardTextChange}
                onActiveFlippingCardTextChange={onActiveFlippingCardTextChange}
                nextMessage={nextMessage}
                onQuizAnswered={setPendingFeedbackFromTabId}
                pendingPlaceholderSx={styles.pendingPlaceholder}
                loadingIndicatorSx={styles.loadingIndicator}
                loadingDotsSx={styles.loadingDots}
              />
            ) : null}
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "6px 20px 8px",
            }}
          >
            <ReactionAndFavorite
              currentReaction={currentReaction}
              markedIds={markedIds}
              isSaved={isSaved}
              isTags={isTags}
              handleReactionClick={handleReactionClick}
              handleAddToFavorite={handleAddToFavorite}
              handleRemoveTag={handleRemoveTag}
              reactionBg={styles.container.background}
            />
            <Typography variant="caption" sx={styles.timestamp}>
              {formattedDateParts ? (
                <>
                  <span>{formattedDateParts.dayMonth}</span>
                  <span style={{ margin: "0 5px" }} />
                  <span>{formattedDateParts.time}</span>
                </>
              ) : null}
            </Typography>
          </Box>
        </Box>
      </Box>
    </>
  );
};
